import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BellRing, Check } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * GlobalServiceBell — bell rendered as a fixed floating "Service Bell" pill
 * on every customer-facing route EXCEPT the QR menu / takeaway pages.
 * Those have their own contextual bell baked into the CustomerPage layout
 * (which is also aware of the cart drawer + sticky place-order bar so the
 * two never overlap). Mounting both at once caused taps near the cart to
 * leak through and accidentally ring the bell — hence this restriction.
 *
 * Self-contained:
 *  - Resolves the table from `localStorage.majnu:lastQrCode` (CustomerPage
 *    persists this whenever a customer scans).
 *  - Resolves identity from cookie (`/api/auth/me`) or, for walk-ins, the
 *    per-device `majnu:guestId` (eagerly seeded if missing). The name is
 *    optional — the backend defaults to "A guest".
 *  - Polls `/api/service-requests/my-table` every 5s.
 *
 * Hidden on dashboard / login / kiosk-display / customer-menu routes.
 */
const SUPPRESSED_ROUTES = [
  '/login',
  '/dashboard',
  '/menu-display',
  '/table/',     // CustomerPage has its own contextual bell — see line ~1353
  '/takeaway',   // CustomerPage handles the takeaway flow too
  // Owner ask Feb 2026: "Don't show service bell on staff and till panel
  // as it's for customers only." All staff workstation routes are
  // suppressed so the floating bell can never appear on a tablet that
  // a customer might glimpse from across the counter.
  '/serving',     // WaiterPanel
  '/waiter',      // legacy alias
  '/bar',         // BarPanel
  '/kitchen',     // PrepPanel
  '/shisha',      // PrepPanel(type=shisha)
  '/dj',          // DJPanel
  '/pos',         // POSPage (till)
];

export default function GlobalServiceBell() {
  const location = useLocation();
  const path = location.pathname;
  const suppressed = SUPPRESSED_ROUTES.some(p => path === p || path.startsWith(p));

  const [table, setTable] = useState(null);
  const [user, setUser] = useState(null);
  const [guestId, setGuestId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  // Bell becomes ready as soon as we know the table + ANY identity hint:
  //  - signed-in member, or
  //  - a guest with a per-device guest_id (the name is optional — backend
  //    accepts "A guest" when no name is supplied).
  const ready = !!table && (!!user || !!guestId);
  const mountedRef = useRef(false);

  // Resolve identity + table context on mount (and on route change in case
  // the customer just registered / logged in on another page).
  useEffect(() => {
    if (suppressed) return;
    let cancelled = false;
    (async () => {
      // Last visited QR — set by CustomerPage on every customer mount.
      let qr = '';
      try { qr = localStorage.getItem('majnu:lastQrCode') || ''; } catch { /* sandboxed */ }
      if (!qr) return;
      // Identity
      let me = null;
      try {
        const { data } = await api.get('/auth/me');
        me = data || null;
      } catch { /* not signed in */ }
      let gId = '';
      let gName = '';
      try {
        // Seed a per-device guest_id eagerly. The customer page does this
        // too, but landing straight on /book or /payment/success skips that
        // path. We want the bell to work even then.
        // Feb 2026: guest_id is now scoped per-QR (`majnu:guestId:<qr>`)
        // so each table scan has its own walk-in identity. Fall back to
        // the legacy global key for tabs opened before the migration.
        const qrKey = qr ? `majnu:guestId:${qr}` : '';
        gId = (qrKey && localStorage.getItem(qrKey)) || localStorage.getItem('majnu:guestId') || '';
        if (!gId) {
          gId = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `g_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        }
        if (qrKey) localStorage.setItem(qrKey, gId);
        gName = (localStorage.getItem(`majnu:guestName:${qr}`) || localStorage.getItem('majnu:guestName:default') || '').trim();
      } catch { /* sandboxed */ }
      // Table
      try {
        const { data } = await api.get(`/tables/qr/${encodeURIComponent(qr)}`);
        if (!cancelled) {
          // 3-hour session enforcement (Feb 2026 owner ask) — when a
          // walk-in guest has gone past 3h since their QR scan, the
          // global bell on other routes (/book, /payment-success, etc.)
          // also disappears. Signed-in members are exempt. Stamp the
          // session if it's the very first time we see this table.
          if (!me && data?.id) {
            const sessKey = `majnu:tableSession:${data.id}`;
            let startedAt = 0;
            try {
              const raw = localStorage.getItem(sessKey);
              if (raw) {
                const parsed = JSON.parse(raw);
                startedAt = Date.parse(parsed?.startedAt || '') || 0;
              } else {
                localStorage.setItem(sessKey, JSON.stringify({
                  startedAt: new Date().toISOString(),
                }));
                startedAt = Date.now();
              }
            } catch { /* sandboxed */ }
            const SESSION_TTL_MS = 3 * 60 * 60 * 1000;
            if (startedAt && (Date.now() - startedAt) > SESSION_TTL_MS) {
              // Stale session — leave `table` null so the bell stays
              // hidden. Re-scanning the QR will refresh `startedAt`
              // via CustomerPage's session-stamp effect.
              return;
            }
          }
          setTable(data);
          setUser(me);
          setGuestId(gId);
          setGuestName(gName);
        }
      } catch { /* invalid or stale QR — bell stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [path, suppressed]);

  // Poll active service request for this table.
  const fetchActive = useCallback(async () => {
    if (!ready) return;
    try {
      const url = user
        ? '/service-requests/my-table'
        : `/service-requests/my-table?guest_id=${encodeURIComponent(guestId)}`;
      const { data } = await api.get(url);
      // Treat empty `{}` (no active request) as null so the idle Service
      // Bell button shows, not the "Service called" pill.
      setRequest(data && data.id ? data : null);
    } catch { /* non-fatal */ }
  }, [ready, user, guestId]);

  useEffect(() => {
    if (!ready) return;
    if (mountedRef.current) {
      fetchActive();
    } else {
      mountedRef.current = true;
      fetchActive();
    }
    const i = setInterval(fetchActive, 5000);
    return () => clearInterval(i);
  }, [ready, fetchActive]);

  const callService = useCallback(async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const payload = { table_id: table.id, request_type: 'help' };
      if (!user) {
        payload.guest_id = guestId;
        // Backend defaults to "A guest" when name is empty — pass through so
        // anonymous walk-ins can still ring without being forced through a
        // name-entry flow.
        if (guestName) payload.customer_name = guestName;
      }
      const { data } = await api.post('/service-requests', payload);
      setRequest(data);
      toast.success("Service called — we're on the way");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not call service');
    } finally {
      setBusy(false);
    }
  }, [ready, busy, table, user, guestId, guestName]);

  const cancelService = useCallback(async () => {
    if (!request || busy) return;
    setBusy(true);
    try {
      const url = user
        ? `/service-requests/${request.id}/cancel`
        : `/service-requests/${request.id}/cancel?guest_id=${encodeURIComponent(guestId)}`;
      await api.put(url);
      setRequest(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusy(false);
    }
  }, [request, busy, user, guestId]);

  const dismissService = useCallback(async () => {
    if (!request || busy) return;
    setBusy(true);
    try {
      const url = user
        ? `/service-requests/${request.id}/dismiss`
        : `/service-requests/${request.id}/dismiss?guest_id=${encodeURIComponent(guestId)}`;
      await api.put(url);
      setRequest(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not dismiss');
    } finally {
      setBusy(false);
    }
  }, [request, busy, user, guestId]);

  if (suppressed || !ready) return null;

  if (!request) {
    return (
      <button
        onClick={callService}
        disabled={busy}
        className="fixed bottom-6 right-4 z-40 flex flex-col items-center gap-1 group disabled:opacity-60 font-sans"
        aria-label="Ring For Service"
        data-testid="global-call-service-button"
      >
        <span className="w-14 h-14 rounded-full bg-gold text-black shadow-lg shadow-gold/40 border-2 border-gold/70 flex items-center justify-center group-hover:scale-110 group-active:scale-95 transition-transform">
          <BellRing className="w-6 h-6" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-gold leading-none drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)]">Ring For<br/>Service</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-4 z-40 max-w-[calc(100vw-2rem)] rounded-full border border-gold/30 bg-bg-secondary/95 backdrop-blur-md py-1.5 pl-2 pr-3 shadow-lg flex items-center gap-2"
      data-testid="global-service-request-status"
    >
      {request.status === 'accepted' ? (
        <>
          <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
            <Check className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <p className="text-white text-xs font-sans truncate max-w-[160px]">
            On the way{request.accepted_by_name ? ` — ${request.accepted_by_name}` : ''}
          </p>
          <button
            onClick={dismissService}
            disabled={busy}
            className="shrink-0 ml-1 px-2 py-1 text-[11px] font-sans rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50 transition-colors"
            data-testid="global-dismiss-service-button"
          >
            Done
          </button>
        </>
      ) : (
        <>
          <div className="w-7 h-7 rounded-full bg-gold/20 flex items-center justify-center shrink-0 animate-pulse">
            <BellRing className="w-3.5 h-3.5 text-gold" />
          </div>
          <p className="text-white text-xs font-sans uppercase tracking-[0.12em]">Service called</p>
          <button
            onClick={cancelService}
            disabled={busy}
            className="shrink-0 ml-1 px-2 py-1 text-[11px] font-sans rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50 transition-colors"
            data-testid="global-cancel-service-button"
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );
}
